<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Chart as ChartJS, ChartArea } from 'chart.js'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Chart from '@uiKit/components/Chart.vue'
import AppTabs from '@uiKit/components/Tabs.vue'
import Card from '@uiKit/components/Card.vue'
import Select from '@uiKit/elements/Select.vue'
import { useThemeTokens } from '@uiKit/composables/useThemeTokens'
import type { AppTab } from '@uiKit/components/Tabs.vue'
import type { SelectOption } from '@shared/types/form'
import type { PortfolioGraphRental, PortfolioGraphValue } from '../types/portfolioTypes'

interface GradientContext {
  chart: ChartJS & { chartArea: ChartArea; ctx: CanvasRenderingContext2D }
}

interface TooltipContext {
  parsed: { y: number | null }
  label: string
}

interface AxisTick {
  value: number
}

type ChartMode = 'value' | 'rental'

interface Props {
  valueData?: PortfolioGraphValue | null
  rentalData?: PortfolioGraphRental | null
}

const props = withDefaults(defineProps<Props>(), {
  valueData: null,
  rentalData: null,
})

// The heading names the series on screen (Figma 33837:237170: "Monthly Rental"
// when the Rental tab is active).
const CHART_TITLE: Record<ChartMode, string> = {
  value: 'Portfolio Value',
  rental: 'Monthly Rental',
}
const FALLBACK_CURRENCY = 'AED'
const CURRENCY_DECIMALS = 2
const THOUSAND = 1000
const MILLION = 1_000_000
const VALUE_FILL_ALPHA = 0.18
// The bar gradient reaches its solid green at 75% of the bar height (design).
const RENTAL_GRADIENT_STOP = 0.75
const HEX_LENGTH = 6

const activeMode = ref<ChartMode>('value')

const chartTabs: AppTab[] = [
  { key: 'value', label: 'Value' },
  { key: 'rental', label: 'Rental' },
]

const chartType = computed(() => (activeMode.value === 'value' ? 'line' : 'bar'))

const { isDark, readToken } = useThemeTokens()

/** `#RRGGBB` → `rgba(…)`. Canvas gradients need a resolved color, not a token. */
function withAlpha(color: string, alpha: number): string {
  const hex = color.replace('#', '')
  if (hex.length !== HEX_LENGTH) return color
  const red = parseInt(hex.slice(0, 2), 16)
  const green = parseInt(hex.slice(2, 4), 16)
  const blue = parseInt(hex.slice(4, 6), 16)
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

const palette = computed(() => {
  // Dark mode inverts the neutral ramp, so the grey used for the dashed
  // "Invested" line and the axis labels is picked per theme.
  const neutralToken = isDark.value ? '--p-surface-500' : '--p-surface-400'
  return {
    line: readToken('--p-primary-400', '#7e79ff'),
    neutral: readToken(neutralToken, '#a4a7ae'),
    rentalFrom: readToken('--p-success-100', '#d8f9eb'),
    rentalTo: readToken('--p-success-500', '#2fc48d'),
    grid: readToken('--p-surface-100', '#f3f4f6'),
    border: readToken('--p-content-border-color', '#e9eaeb'),
    surface: readToken('--p-surface-0', '#ffffff'),
    textStrong: readToken('--p-text-color', '#181d27'),
    textMuted: readToken('--p-text-muted-color', '#717680'),
  }
})

function formatMonth(ym: string): string {
  const [yearStr, monthStr] = ym.split('-')
  const year = Number(yearStr)
  const monthIndex = Number(monthStr) - 1
  return new Date(year, monthIndex, 1).toLocaleString('en', { month: 'short' })
}

// The graph API returns one April–March fiscal-year window per request and
// lists the years it has in `availableFiscalYears`. The series is rendered as
// sent; picking a year updates `fiscalYear` so the container refetches.
// undefined = the backend's default (current) year.
const fiscalYear = defineModel<string | undefined>('fiscalYear')

const FISCAL_YEAR_START_MONTH = 4

/** "2025-04-01" → "2025-26" — labels the window when the API lists no years. */
function fiscalYearOf(isoDate: string): string {
  const [yearStr, monthStr] = isoDate.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  if (!year || !month) return ''
  const startYear = month >= FISCAL_YEAR_START_MONTH ? year : year - 1
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`
}

const fiscalYears = computed<string[]>(() => {
  const listed = props.valueData?.availableFiscalYears ?? []
  if (listed.length) return listed
  const shown = fiscalYearOf(props.valueData?.startDate ?? '')
  return shown ? [shown] : []
})

const fyOptions = computed<SelectOption[]>(() =>
  fiscalYears.value.map((year) => ({ label: `FY ${year}`, value: year })),
)

// Until the user picks, show the year the backend defaulted to (newest first).
const selectedFiscalYear = computed({
  get: () => fiscalYear.value ?? fiscalYears.value[0] ?? '',
  set: (year: string) => {
    fiscalYear.value = year
  },
})

const valueSeries = computed(() => props.valueData?.series ?? [])
const rentalSeries = computed(() => props.rentalData?.series ?? [])

// Every month keeps its x-axis label, including future (null) ones.
const valueLabels = computed(() => valueSeries.value.map((point) => formatMonth(point.month)))
const rentalLabels = computed(() => rentalSeries.value.map((point) => formatMonth(point.month)))

// Nulls go to Chart.js as-is: with spanGaps off the line ends at the last known
// month instead of dropping to zero, and a null month draws no bar.
const portfolioValues = computed(() => valueSeries.value.map((point) => point.portfolioValue))
const investedValues = computed(() => valueSeries.value.map((point) => point.investedAmount))
const rentalValues = computed(() => rentalSeries.value.map((point) => point.rentalIncome))

// An all-zero series makes Chart.js pick a −1…1 axis, which reads as nonsense
// for money. Pinning the axis to zero keeps a flat line sitting on the floor.
const hasNonZeroValues = computed(() => {
  const values =
    activeMode.value === 'rental'
      ? rentalValues.value
      : [...portfolioValues.value, ...investedValues.value]
  return values.some((value) => value !== null && value !== 0)
})

// The API tells us which currency the series is in — never assume AED.
const currencyCode = computed(
  () =>
    (activeMode.value === 'rental' ? props.rentalData?.currency : props.valueData?.currency) ??
    FALLBACK_CURRENCY,
)

function formatAmount(value: number): string {
  try {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: currencyCode.value,
      minimumFractionDigits: CURRENCY_DECIMALS,
      maximumFractionDigits: CURRENCY_DECIMALS,
    }).format(value)
  } catch {
    // Unrecognised currency code from the API — prefix it verbatim instead.
    const amount = value.toLocaleString('en-AE', {
      minimumFractionDigits: CURRENCY_DECIMALS,
      maximumFractionDigits: CURRENCY_DECIMALS,
    })
    return `${currencyCode.value} ${amount}`
  }
}

function trimTrailingZero(value: number): string {
  return String(Number(value.toFixed(1)))
}

// The unit comes from the largest tick so the whole axis reads in one scale
// ("0K … 4K" rather than "0 … 4K").
function formatAxisTick(raw: number | string, ticks: AxisTick[]): string {
  const numeric = Number(raw)
  const largest = ticks.length
    ? Math.max(...ticks.map((tick) => Math.abs(tick.value)))
    : Math.abs(numeric)
  if (largest >= MILLION) return `${trimTrailingZero(numeric / MILLION)}M`
  if (largest >= THOUSAND) return `${trimTrailingZero(numeric / THOUSAND)}K`
  return String(numeric)
}

const chartData = computed(() => {
  const colors = palette.value

  if (activeMode.value === 'rental') {
    return {
      labels: rentalLabels.value,
      datasets: [
        {
          label: 'Monthly Rental Income',
          data: rentalValues.value,
          backgroundColor: (context: GradientContext) => {
            const { ctx, chartArea } = context.chart
            if (!chartArea) return colors.rentalTo
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
            gradient.addColorStop(0, colors.rentalFrom)
            gradient.addColorStop(RENTAL_GRADIENT_STOP, colors.rentalTo)
            gradient.addColorStop(1, colors.rentalTo)
            return gradient
          },
          borderColor: 'transparent',
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    }
  }

  return {
    labels: valueLabels.value,
    datasets: [
      {
        label: 'Portfolio Value',
        data: portfolioValues.value,
        borderColor: colors.line,
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        backgroundColor: (context: GradientContext) => {
          const { ctx, chartArea } = context.chart
          if (!chartArea) return 'transparent'
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
          gradient.addColorStop(0, withAlpha(colors.line, VALUE_FILL_ALPHA))
          gradient.addColorStop(1, withAlpha(colors.line, 0))
          return gradient
        },
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: colors.surface,
        pointHoverBorderColor: colors.line,
        pointHoverBorderWidth: 2.5,
      },
      {
        label: 'Invested',
        data: investedValues.value,
        borderColor: colors.neutral,
        borderWidth: 1.5,
        borderDash: [5, 4],
        tension: 0.4,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: colors.surface,
        pointHoverBorderColor: colors.neutral,
        pointHoverBorderWidth: 2,
      },
    ],
  }
})

const chartOptions = computed(() => {
  const colors = palette.value

  return {
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: colors.surface,
        borderColor: colors.border,
        borderWidth: 1,
        titleColor: colors.textMuted,
        bodyColor: colors.textStrong,
        bodyFont: { weight: 'bold', size: 13 },
        titleFont: { size: 11 },
        padding: 10,
        cornerRadius: 10,
        displayColors: false,
        // Future months have no value — no tooltip row rather than "AED 0.00".
        filter: (context: TooltipContext) => context.parsed.y !== null,
        callbacks: {
          label: (context: TooltipContext) =>
            context.parsed.y === null ? '' : formatAmount(context.parsed.y),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: colors.neutral,
          // Figma: 14px medium axis labels.
          font: { size: 14, weight: 500 },
        },
      },
      y: {
        beginAtZero: activeMode.value === 'rental' || !hasNonZeroValues.value,
        border: { display: false },
        // Bars are read against their labels, not gridlines — design shows the
        // grid on the value line chart only.
        grid: { display: activeMode.value === 'value', color: colors.grid },
        ticks: {
          color: colors.neutral,
          // Figma: 14px medium axis labels.
          font: { size: 14, weight: 500 },
          callback: (raw: number | string, _index: number, ticks: AxisTick[]) =>
            formatAxisTick(raw, ticks),
        },
      },
    },
  }
})
</script>

<template>
  <Card
    variant="outlined"
    direction="col"
    padding="var(--pvc-pad)"
    gap="var(--pvc-gap)"
    class="pvc"
  >
    <!-- Header — title left; Value/Rental toggle + fiscal-year picker right -->
    <Box align="center" gap="3" class="pvc__header">
      <Text type="heading" weight="semibold" is="h2" class="pvc__title">{{
        CHART_TITLE[activeMode]
      }}</Text>
      <AppTabs v-model="activeMode" :tabs="chartTabs" size="sm" class="pvc__tabs" />
      <Select
        v-if="fyOptions.length"
        v-model="selectedFiscalYear"
        :options="fyOptions"
        variant="chip"
        class="pvc__fy"
      />
    </Box>

    <!-- Chart -->
    <Box class="pvc__chart">
      <Chart :type="chartType" :data="chartData" :chart-options="chartOptions" height="250px" />
    </Box>

    <!-- Legend -->
    <Box align="center" justify="center" gap="4" class="pvc__legend">
      <template v-if="activeMode === 'value'">
        <Box align="center" gap="0.375rem">
          <Box class="pvc__legend-dot pvc__legend-dot--value" />
          <Text type="caption" weight="medium" color="muted" is="span" class="pvc__legend-label">
            Portfolio Value
          </Text>
        </Box>
        <Box align="center" gap="0.375rem">
          <Box class="pvc__legend-dot pvc__legend-dot--invested" />
          <Text type="caption" weight="medium" color="muted" is="span" class="pvc__legend-label">
            Invested
          </Text>
        </Box>
      </template>
      <Box v-else align="center" gap="0.375rem">
        <Box class="pvc__legend-dot pvc__legend-dot--rental pvc__legend-dot--sm" />
        <Text type="caption" weight="medium" color="muted" is="span" class="pvc__legend-label">
          Monthly Rental Income
        </Text>
      </Box>
    </Box>
  </Card>
</template>

<style scoped>
/* Padding / gap go through custom properties so the raw values passed to
   Card (inline styles) stay responsive. */
.pvc {
  --pvc-pad: 1.25rem;
  --pvc-gap: 1rem;
  overflow: hidden;
  width: 100%;
}

.pvc__header {
  width: 100%;
}

.pvc__title {
  flex: 1;
  min-width: 0;
}

.pvc__tabs {
  flex-shrink: 0;
}

.pvc__fy {
  flex-shrink: 0;
}

.pvc__chart {
  width: 100%;
}

.pvc__legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50px;
  flex-shrink: 0;
}

.pvc__legend-dot--sm {
  width: 10px;
  height: 10px;
}

.pvc__legend-dot--value {
  background: var(--p-primary-400);
}

.pvc__legend-dot--invested {
  background: var(--p-surface-400);
}

.pvc__legend-dot--rental {
  background: var(--p-success-500);
}

/* Match the dashed "Invested" line, which uses the lighter grey in dark mode */
.p-dark .pvc__legend-dot--invested {
  background: var(--p-surface-500);
}

.pvc__legend-label {
  white-space: nowrap;
}

@media (max-width: 992px) {
  .pvc {
    --pvc-pad: 1rem;
    --pvc-gap: 0.875rem;
  }

  .pvc__chart :deep(canvas) {
    max-height: 220px !important;
  }

  .pvc__header {
    flex-wrap: wrap;
  }

  .pvc__legend {
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .pvc__legend-label {
    white-space: normal;
  }
}
</style>
